import * as React from 'react';

import {
  Col,
  MaterialSymbol,
  TouchButton,
  TouchButtonProps,
  cn,
} from '@rematter/ui-web';

import { useInventoryGroupStyle } from '~/modules/trade/hooks/useInventoryGroupStyle';
import { InventoryItemRecord } from '~/modules/trade/types/load';

interface InventoryItemButtonProps extends Omit<TouchButtonProps, 'children'> {
  inventoryItem: InventoryItemRecord;
  itemSubtitle?: React.ReactNode;
  isLoading?: boolean;
  isDeduction?: boolean;
  overrideGroupStyle?: boolean;
}

export const InventoryItemButton = ({
  inventoryItem,
  itemSubtitle,
  isLoading,
  isDeduction,
  isSelected,
  onPress,
  className,
  overrideGroupStyle = false,
  style,
  leftElement: propLeftElement,
  ...propsTouchButton
}: InventoryItemButtonProps) => {
  const groupStyle = useInventoryGroupStyle({
    colorHue:
      inventoryItem.colorHue ?? inventoryItem.itemGroup?.colorHue ?? undefined,
  });

  const leftElement = React.useMemo(() => {
    if (propLeftElement) {
      return propLeftElement;
    }

    if (inventoryItem.mnemonic) {
      return (
        <MaterialSymbol
          description={inventoryItem.mnemonic}
          isLoading={isLoading}
          isDeduction={isDeduction}
          style={groupStyle}
          className="min-w-[25px] px-1"
        />
      );
    }

    return null;
  }, [
    propLeftElement,
    inventoryItem.mnemonic,
    isLoading,
    isDeduction,
    groupStyle,
  ]);

  const buttonStyle = React.useMemo(
    () => ({
      ...(overrideGroupStyle ? {} : groupStyle),
      ...style,
    }),
    [overrideGroupStyle, groupStyle, style],
  );

  return (
    <TouchButton
      isSelected={isSelected}
      onPress={onPress}
      className={cn(
        '@InventoryItemButton',
        '[--hover-bg:inherit] [--hover-color:inherit]',
        'flex-nowrap hover:!bg-[var(--hover-bg)] hover:!text-[var(--hover-color)]',
        className,
      )}
      style={buttonStyle}
      leftElement={leftElement}
      align="center"
      {...propsTouchButton}
    >
      <Col className="w-full min-w-0">
        <div className="truncate">{inventoryItem.name}</div>
        <div className="flex-none text-xs">{itemSubtitle}</div>
      </Col>
    </TouchButton>
  );
};
